/**
 * Currency allowlist (ISO 4217).
 *
 * `digits` is the number of minor units, taken from ISO 4217 — it is NOT assumed to be
 * 2. Zero-digit currencies (JPY, KRW, CLP, ISK) and three-digit currencies (KWD, BHD)
 * are in the list on purpose so the non-standard paths are exercised in real use and in
 * the test suite.
 *
 * V1 does no currency conversion and no FX lookups. The currency is a label plus a
 * minor-unit rule, nothing more.
 */

export interface CurrencyDef {
  readonly code: string;
  readonly digits: 0 | 2 | 3;
  readonly label: string;
}

export const CURRENCIES = [
  { code: 'EUR', digits: 2, label: 'Euro' },
  { code: 'USD', digits: 2, label: 'US dollar' },
  { code: 'GBP', digits: 2, label: 'British pound' },
  { code: 'CAD', digits: 2, label: 'Canadian dollar' },
  { code: 'AUD', digits: 2, label: 'Australian dollar' },
  { code: 'NZD', digits: 2, label: 'New Zealand dollar' },
  { code: 'CHF', digits: 2, label: 'Swiss franc' },
  { code: 'SEK', digits: 2, label: 'Swedish krona' },
  { code: 'NOK', digits: 2, label: 'Norwegian krone' },
  { code: 'DKK', digits: 2, label: 'Danish krone' },
  { code: 'PLN', digits: 2, label: 'Polish złoty' },
  { code: 'CZK', digits: 2, label: 'Czech koruna' },
  { code: 'HUF', digits: 2, label: 'Hungarian forint' },
  { code: 'RON', digits: 2, label: 'Romanian leu' },
  { code: 'BGN', digits: 2, label: 'Bulgarian lev' },
  { code: 'TRY', digits: 2, label: 'Turkish lira' },
  { code: 'ILS', digits: 2, label: 'Israeli new shekel' },
  { code: 'AED', digits: 2, label: 'UAE dirham' },
  { code: 'ZAR', digits: 2, label: 'South African rand' },
  { code: 'INR', digits: 2, label: 'Indian rupee' },
  { code: 'SGD', digits: 2, label: 'Singapore dollar' },
  { code: 'HKD', digits: 2, label: 'Hong Kong dollar' },
  { code: 'MXN', digits: 2, label: 'Mexican peso' },
  { code: 'BRL', digits: 2, label: 'Brazilian real' },
  { code: 'JPY', digits: 0, label: 'Japanese yen' },
  { code: 'KRW', digits: 0, label: 'South Korean won' },
  { code: 'CLP', digits: 0, label: 'Chilean peso' },
  { code: 'ISK', digits: 0, label: 'Icelandic króna' },
  { code: 'KWD', digits: 3, label: 'Kuwaiti dinar' },
  { code: 'BHD', digits: 3, label: 'Bahraini dinar' },
] as const satisfies readonly CurrencyDef[];

export type CurrencyCode = (typeof CURRENCIES)[number]['code'];

const BY_CODE = new Map<string, CurrencyDef>(CURRENCIES.map((c) => [c.code, c]));

export const DEFAULT_CURRENCY: CurrencyCode = 'EUR';

export function isSupportedCurrency(code: unknown): code is CurrencyCode {
  return typeof code === 'string' && BY_CODE.has(code);
}

export function getCurrency(code: string): CurrencyDef | undefined {
  return BY_CODE.get(code);
}

/**
 * Minor units per major unit, e.g. 100 for EUR, 1 for JPY, 1000 for KWD.
 * Throws on an unsupported code: money maths must never silently guess.
 */
export function minorUnitFactor(code: string): number {
  const currency = BY_CODE.get(code);
  if (!currency) throw new Error(`Unsupported currency: ${code}`);
  return 10 ** currency.digits;
}

export function currencyDigits(code: string): number {
  const currency = BY_CODE.get(code);
  if (!currency) throw new Error(`Unsupported currency: ${code}`);
  return currency.digits;
}
